import { useEffect, useState, useCallback } from 'react';
import { getMySubscription, FranchiseSubscription, syncCurrentSubscription } from '../helpers/api/subscriptions';
import { useUser } from '.';

type UseSubscriptionState = {
    loading: boolean;
    error: string | null;
    subscription: FranchiseSubscription | null;
    isActive: boolean;
    refetch?: () => Promise<void>;
};

export default function useSubscription(): UseSubscriptionState {
    const [loggedInUser] = useUser();
    const [state, setState] = useState<UseSubscriptionState>({
        loading: false,
        error: null,
        subscription: null,
        isActive: false,
    });

    const loadSubscription = useCallback(async () => {
        const role = loggedInUser?.role;
        // Only franchise-bound roles care about subscription status
        if (!role || !['franchise_admin', 'staff'].includes(role)) {
            setState({
                loading: false,
                error: null,
                subscription: null,
                isActive: false,
                refetch: loadSubscription,
            });
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: null }));
        try {
            let sub = await getMySubscription();

            // If subscription exists but period end is missing/invalid, sync once from backend (Stripe-backed).
            const hasInvalidPeriodEnd =
                !!sub &&
                !!sub.stripeSubscriptionId &&
                (!sub.currentPeriodEnd || Number.isNaN(new Date(sub.currentPeriodEnd).getTime()));

            if (hasInvalidPeriodEnd) {
                const synced = await syncCurrentSubscription();
                if (synced) {
                    sub = synced;
                }
            }

            const periodEnd = sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null;
            const endedAfterScheduledCancel =
                !!sub?.cancelledAtPeriodEnd &&
                !!periodEnd &&
                !Number.isNaN(periodEnd.getTime()) &&
                periodEnd.getTime() <= Date.now();
            const isActive = !!(sub && sub.status === 'active' && sub.isPaid && !endedAfterScheduledCancel);

            setState({
                loading: false,
                error: null,
                subscription: sub,
                isActive,
                refetch: loadSubscription,
            });
        } catch (err: any) {
            const message = typeof err === 'string' ? err : err?.message || 'Failed to load subscription';
            setState({
                loading: false,
                error: message,
                subscription: null,
                isActive: false,
                refetch: loadSubscription,
            });
        }
    }, [loggedInUser?.role]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            await loadSubscription();
            if (cancelled) return;
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [loadSubscription]);

    return state;
}

